// ── In-memory store ───────────────────────────────────────────────────────────
const store = {
  players: [],
  tournaments: [],
  matchResults: [],
};

function genId() {
  return crypto.randomUUID();
}

// ── Elo helpers (mirrors backend logic) ──────────────────────────────────────
function kFactor(gamesPlayed) {
  return gamesPlayed < 30 ? 32 : 16;
}

function expectedScore(eloA, eloB) {
  return 1.0 / (1.0 + Math.pow(10, (eloB - eloA) / 400.0));
}

function calcNewElo(current, expected, actual, gamesPlayed) {
  const k = kFactor(gamesPlayed);
  return Math.max(
    600,
    Math.min(1400, Math.round(current + k * (actual - expected))),
  );
}

// ── Players ───────────────────────────────────────────────────────────────────
export const guestPlayersApi = {
  getAll: () => {
    const results = store.matchResults;
    const players = store.players
      .map((p) => {
        const wins = results.filter((r) => r.winnerName === p.name).length;
        const losses = results.filter((r) => r.loserName === p.name).length;
        const total = wins + losses;
        const winPct = total > 0 ? wins / total : -1;
        return { ...p, wins, losses, winPct };
      })
      .sort((a, b) => {
        if (a.elo != null && b.elo != null) return b.elo - a.elo;
        if (a.elo != null) return -1;
        if (b.elo != null) return 1;
        return a.name.localeCompare(b.name);
      });
    return Promise.resolve(players);
  },

  getById: (id) =>
    Promise.resolve(store.players.find((p) => p.id === id) ?? null),

  create: (player) => {
    const newPlayer = {
      id: genId(),
      name: player.name,
      elo: null,
      gamesPlayed: 0,
      createdAt: new Date().toISOString(),
    };
    store.players.push(newPlayer);
    return Promise.resolve(newPlayer);
  },

  update: (id, updated) => {
    const p = store.players.find((p) => p.id === id);
    if (!p) throw new Error("Player not found");
    p.name = updated.name;
    return Promise.resolve(p);
  },

  delete: (id) => {
    store.players = store.players.filter((p) => p.id !== id);
    return Promise.resolve(null);
  },
};

// ── Tournaments ───────────────────────────────────────────────────────────────
export const guestTournamentsApi = {
  getAll: () =>
    Promise.resolve(
      [...store.tournaments].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      ),
    ),

  getById: (id) =>
    Promise.resolve(store.tournaments.find((t) => t.id === id) ?? null),

  create: (tournament) => {
    const newT = {
      id: genId(),
      name: tournament.name,
      status: 0,
      winner: "",
      runnerUp: "",
      playerIds: [],
      format: "single",
      bracketState: null,
      createdAt: new Date().toISOString(),
    };
    store.tournaments.push(newT);
    return Promise.resolve(newT);
  },

  update: (id, updated) => {
    const t = store.tournaments.find((t) => t.id === id);
    if (!t) throw new Error("Tournament not found");
    Object.assign(t, {
      name: updated.name ?? t.name,
      status: updated.status ?? t.status,
      winner: updated.winner ?? t.winner,
      runnerUp: updated.runnerUp ?? t.runnerUp,
      playerIds: updated.playerIds ?? t.playerIds,
      format: updated.format ?? t.format,
      bracketState:
        updated.bracketState !== undefined
          ? updated.bracketState
          : t.bracketState,
    });
    return Promise.resolve(t);
  },

  delete: (id) => {
    store.tournaments = store.tournaments.filter((t) => t.id !== id);
    return Promise.resolve(null);
  },
};

// ── Match Results ─────────────────────────────────────────────────────────────
export const guestMatchResultsApi = {
  create: (result) => {
    const newResult = {
      id: genId(),
      winnerName: result.winnerName,
      loserName: result.loserName,
      tournamentId: result.tournamentId,
      tournamentName: result.tournamentName,
      roundNumber: result.roundNumber ?? 0,
      matchIndex: result.matchIndex ?? 0,
      winnerScore: result.winnerScore ?? null,
      loserScore: result.loserScore ?? null,
      createdAt: new Date().toISOString(),
    };
    store.matchResults.push(newResult);

    // ── Update Elo for both players ─────────────────────────────────────────
    const winner = store.players.find((p) => p.name === result.winnerName);
    const loser = store.players.find((p) => p.name === result.loserName);

    if (winner && loser) {
      const winnerElo = winner.elo ?? 1000;
      const loserElo = loser.elo ?? 1000;

      const expWinner = expectedScore(winnerElo, loserElo);
      const expLoser = expectedScore(loserElo, winnerElo);

      winner.elo = calcNewElo(winnerElo, expWinner, 1.0, winner.gamesPlayed);
      winner.gamesPlayed += 1;

      loser.elo = calcNewElo(loserElo, expLoser, 0.0, loser.gamesPlayed);
      loser.gamesPlayed += 1;
    }

    return Promise.resolve(newResult);
  },

  getAll: () => {
    const results = [...store.matchResults]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map((m) => ({
        winnerName: m.winnerName,
        loserName: m.loserName,
        tournamentId: m.tournamentId,
        tournamentName: m.tournamentName,
        createdAt: m.createdAt,
      }));
    return Promise.resolve(results);
  },

  getByPlayer: (playerName) => {
    const results = store.matchResults
      .filter((m) => m.winnerName === playerName || m.loserName === playerName)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map((m) => ({
        opponent: m.winnerName === playerName ? m.loserName : m.winnerName,
        result: m.winnerName === playerName ? "WIN" : "LOSS",
        tournamentName: m.tournamentName,
        createdAt: m.createdAt,
      }));
    return Promise.resolve(results);
  },

  getByTournament: (tournamentId) => {
    const results = store.matchResults
      .filter((m) => m.tournamentId === tournamentId)
      .sort(
        (a, b) => a.roundNumber - b.roundNumber || a.matchIndex - b.matchIndex,
      )
      .map((m) => ({
        winnerName: m.winnerName,
        loserName: m.loserName,
        roundNumber: m.roundNumber,
        matchIndex: m.matchIndex,
        winnerScore: m.winnerScore ?? null,
        loserScore: m.loserScore ?? null,
      }));
    return Promise.resolve(results);
  },

  deleteAll: () => {
    store.matchResults = [];
    return Promise.resolve(null);
  },
};
